import { rd, RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { PopoverContentProps } from '@radix-ui/react-popover';
import { ReactElement, ReactNode } from 'react';
import { cn } from 'v5/platform/dom/cn';
import {
  Combobox,
  ComboboxContent,
  ComboboxCreateItem,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  ComboboxTrigger
} from 'v5/design-system/Combobox';
import { CommandProps } from 'v5/design-system/Command';

export interface ItemSelectorProps<E> {
  config: {
    // use injectConfig
    useOptions: (
      query: string,
      isDropdownOpen: boolean
    ) => RemoteData<
      ({
        id: string;
        label: string;
        disabled?: boolean;
        content?: ReactNode;
        // use it, or use selectedOptions - what is more convenient for you
        checked?: boolean;
      } & (E extends void ? {} : { extra: E }))[]
    >; // tbd if <T> with resolvers
    createContent?: ReactNode;
    emptyContent?: ReactNode;
  };
  placeholder?: string;
  children: ReactElement;
  allowCreate?: boolean;
  closeOnSelect?: boolean;
  onCreate?: (query: string) => Promise<void>;
  onSelect?: (
    id: string,
    wasChecked: boolean,
    extra: E
  ) => void | Promise<void>;
  onBackspaceWhenEmptyQuery?: () => void;
  selectedOptions?: string[];
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  popoverClassName?: string;
  size?: CommandProps['size'];
}

export function ItemSelector<E = void>({
  align = 'start',
  side = 'bottom',
  ...props
}: ItemSelectorProps<E>) {
  const promise = promiseState.useMutationArray<
    string,
    {
      id: string;
      wasChecked: boolean;
      extra: E;
    },
    void
  >(
    async request =>
      props.onSelect?.(request.id, request.wasChecked, request.extra),
    r => r.id
  );

  return (
    <Combobox>
      <ComboboxTrigger asChild>{props.children}</ComboboxTrigger>
      <ComboboxContent
        className={cn('p-0', props.popoverClassName)}
        align={align}
        side={side}
        size={props.size}
      >
        <ComboboxInput placeholder={props.placeholder} />
        <ComboboxList>
          <ComboboxQueryRenderer emptyContent={props.config.emptyContent}>
            {(query, isOpen) => {
              const options = props.config.useOptions(query, isOpen);
              return rd.map(options, options => (
                <ComboboxGroup>
                  {options.map(option => (
                    <ComboboxItem
                      value={option.id}
                      disabled={option.disabled}
                      isSelected={option.checked ?? false}
                      mutation={promise.get(option.id)}
                      closeOnSelect={props.closeOnSelect}
                      onSelect={async () =>
                        await promise.track({
                          id: option.id,
                          wasChecked:
                            option.checked ??
                            props.selectedOptions?.includes(option.id) ??
                            false,
                          extra: ('extra' in option
                            ? option.extra
                            : void 0) as E
                        })
                      }
                    >
                      {option.content ?? option.label}
                    </ComboboxItem>
                  ))}
                </ComboboxGroup>
              ));
            }}
          </ComboboxQueryRenderer>
          {props.allowCreate && (
            <ComboboxCreateItem onSelect={props.onCreate}>
              {props.config.createContent}
            </ComboboxCreateItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
